import { useMemo, useCallback, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { get, map, pick, pickBy, truncate, identity, compact, size } from "lodash"
import pluralize from "pluralize"
import { Paper, IconButton, Tooltip } from "@mui/material"
import { FileCopy, Check } from "@mui/icons-material"
import { copyToClipboard } from "helpers/clipboard"
import { useTrack } from "helpers/analytics"
import { USER, ROLE, TOKEN } from "helpers/shapes"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"
import { getInitialExpirationDate, getExpirationWarning, getEarliestExpirationDate, formatTokenExpirationDate, isExpired } from "./helpers"
import TokenConfirmationDialog from "./components/token-confirmation-dialog"
import RollTokenDialog from "./components/roll-token-dialog"
import FeaturePermissionsDialog, { updatePermissions, FEATURE } from "components/feature-permissions"

function formatUser(currentUser) {
  return (rowUser) => {
    if (!rowUser) { return "NA" }
    return rowUser.id === currentUser.id ? "Me" : rowUser.name
  }
}

function Tokens({
  account,
  addNotification,
  features,
  openTokenFeatures,
  resetCollections,
  role,
  tabs,
  accountFeatures,
  tokenFeatures,
  isTokenFeaturesLoading,
  tokens,
  update,
  user,
  isSuperuser,
}) {
  const [token, setToken] = useState(null)
  const [tempToken, setTempToken] = useState(null)
  const [dialog, setDialog] = useState(null)
  const [currentId, setCurrentId] = useState(null)
  const [idCopied, setIdCopied] = useState(false)

  const handleCopyId = (value) => {
    setCurrentId(value)
    setIdCopied(true)
    copyToClipboard(value, () => setIdCopied(false))
  }

  const isAdmin = role === "admin"
  const earliestDate = getEarliestExpirationDate()

  async function handleFeaturePermissionsSubmit(data) {
    const onError = (error) => addNotification("error", null, error)
    const onSuccess = (results) => {
      addNotification("success", null, `Updated ${size(results)} ${pluralize("feature", size(results))} permissions. (${token.name})`)
      setDialog(null)
    }
    await updatePermissions(data, features, tokenFeatures, account, token, "tokens", update, onError, onSuccess)
  }

  const addForm = useMemo(() => {
    if (!isAdmin) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($name: String!, $expiresOn: Date) {
        createToken(token: { name: $name, expiresOn: $expiresOn }) {
          id
          secret
        }
      }`

      // We can't reload before displaying the token to the user, so instead of using reloadData
      // we manually trigger the resetCollections action after we've assigned the new token to state
      update(query, data, account, {}, (errors, result) => {
        if (!errors) {
          setTempToken(get(result, "data.createToken"))
          setDialog("confirmation")
          resetCollections(["tokens"])
        }
        cb(errors, result)
      })
    }

    return {
      title: "Create Token",
      submitText: "Create Token",
      track: {
        type: "permissioned_token_created",
        payload: ({ name }) => ({ name }),
      },
      onSubmit: onAdd,
      fields: [{
        id: "name",
        label: "Name",
        type: "string",
        validations: {
          isRequired: true,
          minLength: 1,
          maxLength: 50,
        },
      }, {
        id: "expiresOn",
        label: "Expiration date",
        type: "date",
        validations: {
          minDate: earliestDate,
        },
      }],
    }
  }, [account, earliestDate, isAdmin, resetCollections, update])

  const editForm = useMemo(() => {
    function onEdit(data, cb) {
      const query = `mutation ($id: Id!, $name: String!, $expiresOn: Date) {
        updateToken(id: $id, update: { name: $name, expiresOn: $expiresOn }) { ok }
      }`

      update(query, pickBy(data, identity), account, {
        reloadData: "tokens",
      }, cb)
    }

    return {
      title: "Edit Token",
      message: getExpirationWarning,
      submitText: "Save",
      track: {
        type: "permissioned_token_edited",
        payload: ({ name }) => ({ name }),
      },
      onSubmit: onEdit,
      getInitialValues: (token) => ({
        ...pick(token, ["id", "name"]),
        expiresOn: getInitialExpirationDate(token),
      }),
      isEnabled: () => isAdmin,
      fields:
        [{
          id: "name",
          label: "Name",
          type: "string",
          validations: {
            isRequired: true,
            minLength: 1,
            maxLength: 50,
          },
        }, {
          id: "expiresOn",
          label: "Expiration date",
          type: "date",
          validations: {
            minDate: earliestDate,
          },
        }],
    }
  }, [account, earliestDate, isAdmin, update])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation {
        ${map(data, (token, index) => `token${index}: deleteToken (id: "${token.id}") { ok }`)}
      }`

      update(query, null, account, {
        // We also clear any loaded tokens
        reloadData: "tokens",
      }, cb)
    }

    return {
      type: "token",
      track: {
        type: "permissioned_token_deleted",
        payload: ({ id, name }) => ({ id, name }),
      },
      renderItem: token => <li key={token.id}>{token.name}</li>,
      onSubmit: onDelete,
      isEnabled: () => isAdmin,
    }
  }, [account, isAdmin, update])

  const linkTo = useCallback((member) => {
    return `/${account}/home/tokens/${member.id}`
  }, [account])

  const columns = [{
    name: "Name",
    id: "name",
    ...(isAdmin && { linkTo }),
  }, {
    name: "Token ID",
    id: "id",
    render: (token) => {
      const isTokenCopied = currentId === token.id && idCopied
      return <span className="token-id-column">
        <Tooltip title={token.id}><span>{truncate(token.id, { length: 8 })}</span></Tooltip>
        <IconButton className={classNames("copy-id", { success: isTokenCopied })} disabled={isTokenCopied} size="medium" color="primary" onClick={() => handleCopyId(token.id)}>
          {isTokenCopied ? <Check className="success" /> : <FileCopy />}
        </IconButton>
      </span>
    },
  }, {
    name: "Secret",
    id: "prefix",
    formatter: p => `${p}...`,
  }, {
    name: "Created",
    id: "createdAt",
    formatter: formatTokenExpirationDate,
  }, {
    name: "Created By",
    id: "createdBy",
    formatter: formatUser(user),
  }, {
    name: "Last Used",
    id: "lastUsedAt",
    formatter: formatTokenExpirationDate,
  }, {
    name: "Expires",
    id: "expiresOn",
    formatter: formatTokenExpirationDate,
  }]

  const trackRollToken = useTrack({
    type: "token_rolled",
    payload: ({ id, name }) => ({ id, name }),
  })

  const handleRollToken = (data) => {
    const { id, name } = data
    const query = `mutation ($id: Id!) {
      rollToken(id: $id) { id secret }
    }`

    // We can't reload before displaying the token to the user, so instead of using reloadData
    // we manually trigger the resetCollections action after we've assigned the new token to state
    update(query, { id, name }, account, {}, (errors, result) => {
      if (!errors) {
        setTempToken(get(result, "data.rollToken"))
        trackRollToken({ id, name })
        resetCollections(["tokens"])
        setDialog("confirmation")
      }
    })
  }

  function handleShowDialog(dialog) {
    return (token) => {
      setToken(token)
      setDialog(dialog)
    }
  }

  function hideDialog() {
    setTempToken(null)
    setToken(null)
    setDialog(null)
  }

  const additionalOptions = compact([{
    id: "rollToken",
    label: "Roll Token...",
    displayFor: () => isAdmin,
    disableFor: (token) => isExpired(token),
    disabledText: "Cannot roll an expired token",
    onClick: handleShowDialog("roll"),
  },
  isSuperuser && features && {
    id: "manage-features",
    label: "Manage features access",
    onClick: (token) => {
      openTokenFeatures(token.id)
      handleShowDialog("features")(token)
    },
  }])

  return <div className="modules_tokens">
    <UpsellTabs id="tokens" tabs={tabs} />
    <Paper className="content" square>
      <Table
        stickyHeaders
        title="Tokens"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={tokens}
        columns={columns}
        additionalOptions={additionalOptions}
      />
    </Paper>
    <TokenConfirmationDialog showDialog={dialog === "confirmation"} token={tempToken} onClose={hideDialog} />
    <RollTokenDialog showDialog={dialog === "roll"} token={token} onSubmit={handleRollToken} onClose={hideDialog} />
    <FeaturePermissionsDialog show={dialog === "features"} onSubmit={handleFeaturePermissionsSubmit} onClose={hideDialog} entity={token} features={features} accountFeatures={accountFeatures} entityFeatures={tokenFeatures} entityLabel="token" isLoading={isTokenFeaturesLoading} />
  </div>
}

Tokens.propTypes = {
  account: PropTypes.string.isRequired,
  addNotification: PropTypes.func.isRequired,
  features: PropTypes.arrayOf(FEATURE),
  openTokenFeatures: PropTypes.func.isRequired,
  resetCollections: PropTypes.func.isRequired,
  role: ROLE.isRequired,
  tabs: UPSELL_TABS_SHAPE,
  accountFeatures: PropTypes.arrayOf(FEATURE),
  tokenFeatures: PropTypes.arrayOf(FEATURE),
  tokens: PropTypes.arrayOf(TOKEN),
  update: PropTypes.func.isRequired,
  user: USER.isRequired,
  isSuperuser: PropTypes.bool.isRequired,
  isTokenFeaturesLoading: PropTypes.bool,
}

export default Tokens
